import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  Box, Button, Typography, Paper, Table, TableBody, TableContainer,
  TableHead, TableRow, TableCell,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import SimCardDownloadIcon from "@mui/icons-material/SimCardDownload";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Breadcrumb } from "../../reusableComponent/Reusables";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";
import salesOrderService from "../../../services/sales_order";

const fmtDate = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "N/A");
const fmtQty = (n) => (Number(n) || 0).toFixed(2);
const thSx = { background: "#1C3A5E", color: "#fff", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap", padding: "8px 10px" };
const tdSx = { fontSize: "12px", padding: "6px 10px" };

const SOSummaryReport = () => {
  const printRef = useRef(null);
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const fetchData = async () => {
    if (!fromDate || !toDate) { setMessage("Please select both dates"); return; }
    if (toDate.isBefore(fromDate, "day")) { setMessage("To date cannot be before From date"); return; }
    setLoading(true);
    try {
      const data = await salesOrderService.getSoSummary({
        from_date: fromDate.format("YYYY-MM-DD"),
        to_date: toDate.format("YYYY-MM-DD"),
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
      setMessage("Error fetching SO summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const totals = useMemo(() => rows.reduce((a, r) => ({
    ordered: a.ordered + (Number(r.ordered_qty) || 0),
    delivered: a.delivered + (Number(r.delivered_qty) || 0),
    balance: a.balance + (Number(r.balance_qty) || 0),
  }), { ordered: 0, delivered: 0, balance: 0 }), [rows]);

  const rangeLabel = fromDate.isSame(toDate, "day") ? fmtDate(fromDate) : `${fmtDate(fromDate)} to ${fmtDate(toDate)}`;

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `SO_Summary_${fromDate.format("DDMMYYYY")}`,
    pageStyle: "@page { size: A4 landscape; margin: 8mm; }",
  });

  const handleExcel = () => {
    if (rows.length === 0) { setMessage("No data to download"); return; }
    const data = rows.map((r, i) => ({
      "Sr No": i + 1,
      "Customer Name": r.customer_name || "",
      "SO Number": r.so_no || "",
      "Product Code & Description": `${r.prod_code || ""} - ${r.prod_description || ""}`,
      "PO No & Date": `${r.po_no || "N/A"} / ${fmtDate(r.po_date)}`,
      "Ordered Qty": Number(r.ordered_qty) || 0,
      "Delivered Qty": Number(r.delivered_qty) || 0,
      "Balance Qty": Number(r.balance_qty) || 0,
    }));
    data.push({ "Sr No": "", "Customer Name": "TOTAL", "SO Number": "", "Product Code & Description": "",
      "PO No & Date": "", "Ordered Qty": totals.ordered, "Delivered Qty": totals.delivered, "Balance Qty": totals.balance });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SO Summary");
    XLSX.writeFile(wb, `SO_Summary_${fromDate.format("DDMMYYYY")}_${toDate.format("DDMMYYYY")}.xlsx`);
  };

  const headers = ["Sr No", "Customer Name", "SO Number", "Product Code & Description", "PO No & Date", "Ordered Qty", "Delivered Qty", "Balance Qty"];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ background: "#F4F7FB", minHeight: "100vh", padding: "16px 24px 40px" }}>
        <Breadcrumb routeSegments={[{ name: "SO Summary" }]} />
        <Typography sx={{ fontSize: "16px", fontWeight: 700, mb: 2 }}>SO SUMMARY (DAY WISE)</Typography>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 2, background: "#fff", p: 1.5, border: "1px solid #E2E8F0", borderRadius: "8px" }}>
          <DatePicker label="From Date" format="DD/MM/YYYY" value={fromDate} onChange={(v) => v && setFromDate(v)}
            slotProps={{ textField: { size: "small", sx: { width: 160 } } }} />
          <DatePicker label="To Date" format="DD/MM/YYYY" value={toDate} onChange={(v) => v && setToDate(v)}
            slotProps={{ textField: { size: "small", sx: { width: 160 } } }} />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={fetchData} disabled={loading} sx={{ textTransform: "none" }}>
            {loading ? "Loading..." : "Search"}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" startIcon={<SimCardDownloadIcon />} onClick={handleExcel} sx={{ textTransform: "none" }}>Download Excel</Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ textTransform: "none" }}>Print</Button>
        </Box>

        <Paper ref={printRef} sx={{ p: 2, boxShadow: "none", border: "1px solid #E2E8F0" }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>TRI GASES PVT. LTD.</Typography>
            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>SO Summary &mdash; {rangeLabel}</Typography>
          </Box>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {headers.map((h, i) => (
                    <TableCell key={h} align={i >= 5 ? "right" : "left"}
                      sx={{ ...thSx, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={tdSx}>
                      {loading ? "Loading..." : "No sales orders found for the selected date(s)"}
                    </TableCell>
                  </TableRow>
                ) : rows.map((r, i) => (
                  <TableRow key={`${r.so_no}-${r.prod_code}-${i}`}>
                    <TableCell sx={tdSx}>{i + 1}</TableCell>
                    <TableCell sx={tdSx}>{r.customer_name || "N/A"}</TableCell>
                    <TableCell sx={tdSx}>{r.so_no}</TableCell>
                    <TableCell sx={tdSx}>{r.prod_code}{r.prod_description ? ` - ${r.prod_description}` : ""}</TableCell>
                    <TableCell sx={tdSx}>{r.po_no || "N/A"} / {fmtDate(r.po_date)}</TableCell>
                    <TableCell align="right" sx={tdSx}>{fmtQty(r.ordered_qty)}</TableCell>
                    <TableCell align="right" sx={tdSx}>{fmtQty(r.delivered_qty)}</TableCell>
                    <TableCell align="right" sx={{ ...tdSx, fontWeight: 600, color: r.balance_qty > 0 ? "#C53030" : "#2F855A" }}>{fmtQty(r.balance_qty)}</TableCell>
                  </TableRow>
                ))}
                {rows.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ ...tdSx, fontWeight: 700 }}>Total</TableCell>
                    <TableCell align="right" sx={{ ...tdSx, fontWeight: 700 }}>{fmtQty(totals.ordered)}</TableCell>
                    <TableCell align="right" sx={{ ...tdSx, fontWeight: 700 }}>{fmtQty(totals.delivered)}</TableCell>
                    <TableCell align="right" sx={{ ...tdSx, fontWeight: 700 }}>{fmtQty(totals.balance)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <ReusableSnackbar
          open={!!message}
          onClose={(e, reason) => { if (reason !== "clickaway") setMessage(""); }}
          message={message}
          autoHideDuration={2500}
          vertical="top"
          horizontal="right"
        />
      </Box>
    </LocalizationProvider>
  );
};

export default SOSummaryReport;